import { Error, FormContent } from './Form.styles';

import { AddressAutocomplete } from './AddressAutocomplete';
import { FileInputButton } from './FileInputButton';
import { FormButton } from './Button';
import { FormInput } from './Input';
import { FormInputDate } from './InputDate';
import { FormInputDateAndTime } from './InputDateAndTime';
import { FormInputText } from './InputText';
import { FormInputTime } from './InputTime';
import { FormProps } from './types';
import { FormProvider } from 'react-hook-form';
import { FormSelect } from './Select';
import { ImageInput } from './ImageInput';
import { InputToggle } from './InputToggle';
import { InputWithIcon } from './InputWithIcon';
import { PunchupFormInputDateAndTime } from 'features/common/components/Punchup/composites/forms/PunchupInputDateAndTime';
import { PunchupFormSelect } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSelect';
// Legacy red-zone consumers (punchup CreateEvent KeyDetails, CreateTicket) reach PunchupInput via this
// Form.Punchup* shim and rely on its defaultValue seeding + number onWheel guard; the green primitive
// dropped both, so the shim points at the verbatim local input. Green code imports the green primitive directly.
import { PunchupInput } from './PunchupInput';
import { PunchupInputToggle } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInputToggle';
import { PunchupTextArea } from 'features/common/components/Punchup/primitives/forms/fields/PunchupTextArea';
import { ShowDescriptionInput } from './ShowDescriptionInput';
import { TimezoneSelect as PunchupTimezone } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupTimeZone';
import { RadioButtonList } from './RadioButtonList';
import { TimezoneSelect } from './TimezoneSelect/TimezoneSelect';
import { scrollFirstErrorIntoView } from 'features/common/utils/formErrors';
import { useRef } from 'react';

const Form = ({ children, formMethods, onSubmit, ...props }: FormProps) => {
	const {
		formState: { errors },
		handleSubmit,
	} = formMethods;
	const formRef = useRef<HTMLFormElement>(null);

	return (
		<FormProvider {...formMethods}>
			<FormContent
				{...props}
				ref={formRef}
				// A Save that fails validation must not look like nothing happened: the first message may sit
				// far above the button, and RHF's own focus-on-error never reaches the setValue-driven pickers.
				onSubmit={handleSubmit(onSubmit, () => scrollFirstErrorIntoView(formRef.current))}
			>
				{children}
			</FormContent>
			{errors?.general && <Error>{errors.general.message}</Error>}
		</FormProvider>
	);
};

Form.displayName = 'CustomForm';
Form.Button = FormButton;
Form.Input = FormInput;
Form.InputDate = FormInputDate;
Form.InputDateAndTime = FormInputDateAndTime;
Form.InputTime = FormInputTime;
Form.Select = FormSelect;
Form.ImageInput = ImageInput;
Form.InputWithIcon = InputWithIcon;
Form.AddressAutocomplete = AddressAutocomplete;
Form.InputText = FormInputText;
Form.RadioButtonList = RadioButtonList;
Form.FileInputButton = FileInputButton;
Form.InputToggle = InputToggle;
Form.TimeZone = TimezoneSelect;
Form.PunchupInput = PunchupInput;
Form.PunchupSelect = PunchupFormSelect;
Form.PunchupTimezone = PunchupTimezone;
Form.PunchupTextArea = PunchupTextArea;
Form.PunchupInputDateAndTime = PunchupFormInputDateAndTime;
Form.PunchupToggle = PunchupInputToggle;
Form.ShowDescription = ShowDescriptionInput;

export { Form };
